import { useState, useEffect, useMemo, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, Plus, RefreshCw, Info, Sparkles } from 'lucide-react';
import { api } from '../api/client';
import type {
  Project,
  RcajoInventory,
  RcajoCapability,
  RcajoResource,
  StrategicAssessment,
  StrategicCapability,
  StrategicResource,
  SequencingAssessment,
} from '../types';
import { BareTextarea, parseJSON, parseDeliverables } from './ProjectForm';
import type { FormState } from './ProjectForm';
import { milestoneHealth, HEALTH_TITLE_KEY, HEALTH_BADGE_CLASS } from '../utils/executionHealth';
import { getExecutionProgress } from '../utils/stages';
import { useT } from '../i18n';
import { incomingLinksFor } from '../utils/projectLinks';
import type { ProjectLinkView } from '../utils/projectLinks';

export interface PanelProps {
  form: FormState;
  setForm: (f: FormState) => void;
  projectId: number;
  canEdit: boolean;
}

// ── helpers ──

function scoreLabel(n: number | null | undefined): string {
  return n == null || Number.isNaN(n) ? '—' : String(n);
}

// Lightweight fuzzy: substring (earlier = better) beats subsequence beats no-match.
function fuzzyScore(query: string, text: string): number {
  const q = query.trim().toLowerCase();
  const t = (text || '').toLowerCase();
  if (!q) return 1;
  const idx = t.indexOf(q);
  if (idx >= 0) return 1000 - idx;
  let i = 0;
  for (const ch of t) { if (ch === q[i]) i++; if (i === q.length) break; }
  return i === q.length ? 1 : 0;
}

// 4-segment criticality indicator (0–1 → 0–4 filled segments). Right-aligned in its column.
function CriticalityBar({ value }: { value: number | null }) {
  const { t } = useT();
  const SEGMENTS = 4;
  const filled = value == null || Number.isNaN(value)
    ? 0
    : Math.max(0, Math.min(SEGMENTS, Math.round(value * SEGMENTS)));
  const label = value == null ? '—' : value.toFixed(2);
  return (
    <span className="criticality" title={t('prequal.criticalityTitle', { label })}>
      <span className="criticality-bar">
        {[...Array(SEGMENTS)].map((_, i) => (
          <span key={i} className={`criticality-seg${i < filled ? ' on' : ''}`} />
        ))}
      </span>
      <span className="criticality-num">{label}</span>
    </span>
  );
}

// Type-to-search add control with fuzzy matching against RCAJO/project options,
// plus an "add as new" affordance. Frameless input + design-kit dropdown.
function FuzzyAdd<T extends { id: number }>({
  placeholder, options, getLabel, getSubLabel, onPickExisting, onAddNew, allowNew = true, createText,
}: {
  placeholder: string;
  options: T[];
  getLabel: (o: T) => string;
  getSubLabel?: (o: T) => string | null;
  onPickExisting: (o: T) => void;
  onAddNew?: (name: string) => void;
  allowNew?: boolean;
  createText?: (q: string) => string;
}) {
  const { t } = useT();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Show EVERY option when nothing is typed; fuzzy-rank when there's a query.
  const matches = q.trim()
    ? options.map(o => ({ o, s: fuzzyScore(q, getLabel(o)) })).filter(x => x.s > 0).sort((a, b) => b.s - a.s).map(x => x.o)
    : options;
  const exact = options.find(o => getLabel(o).trim().toLowerCase() === q.trim().toLowerCase());

  const pick = (o: T) => { onPickExisting(o); setQ(''); setOpen(false); };
  const create = () => { const x = q.trim(); if (!x || !onAddNew) return; onAddNew(x); setQ(''); setOpen(false); };

  return (
    <div className="pq-fuzzy" ref={ref}>
      <input
        className="form-input--bare pq-fuzzy-input"
        placeholder={placeholder}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (!q.trim()) return;
            if (exact) pick(exact); else if (allowNew) create();
          }
        }}
      />
      {open && (matches.length > 0 || (allowNew && q.trim())) && (
        <div className="autosuggest-dropdown">
          {matches.map((o) => {
            const sub = getSubLabel?.(o);
            return (
              <div key={o.id} className="autosuggest-option" onClick={() => pick(o)}>
                {getLabel(o)}{sub ? <span className="text-ink-3"> · {sub}</span> : null}
              </div>
            );
          })}
          {allowNew && q.trim() && !exact && (
            <div className="autosuggest-option autosuggest-create" onClick={create}>
              <Plus className="icon-sm" style={{ verticalAlign: 'middle', marginRight: 4 }} />
              {createText ? createText(q.trim()) : t('prequal.addAsNew', { q: q.trim() })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Small info button that toggles an inline RCAJO description block.
// Click-to-open info popover anchored AT the icon (portal + fixed position, so it
// never clips inside a card and appears right where you clicked). Used for ALL
// info icons on the page for consistency.
function InfoPopover({ label, children }: { label?: string; children: ReactNode }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (popRef.current?.contains(e.target as Node) || btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    // Close on scroll/resize of the page (fixed pos would drift) — but not on
    // scrolling the popover's own content, which also bubbles up as a scroll event.
    const onMove = (e: Event) => {
      if (popRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open]);

  const toggle = () => {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left, window.innerWidth - 360)) });
    }
    setOpen(o => !o);
  };

  return (
    <>
      <button ref={btnRef} type="button" className={`pq-info-btn${open ? ' on' : ''}`} title={label || t('common.details')} onClick={toggle}>
        <Info size={13} />
      </button>
      {open && pos && createPortal(
        <div ref={popRef} className="pq-popover" style={{ top: pos.top, left: pos.left }}>{children}</div>,
        document.body,
      )}
    </>
  );
}

// Column header so the Maturity / Criticality numbers below are legible & comparable.
function ResourceHeader() {
  const { t } = useT();
  return (
    <div className="pq-res pq-res-head">
      <span className="pq-res-main">{t('prequal.resource')}</span>
      <span className="pq-res-mat" title={t('prequal.maturityHeaderTitle')}>{t('prequal.maturityArrow')}</span>
      <span className="pq-res-crit" title={t('prequal.criticalityHeaderTitle')}>{t('prequal.criticality')}</span>
      <span className="pq-res-act" />
    </div>
  );
}

// One resource row: name (+ RCAJO info) | maturity current→target | criticality | remove.
// Columns are fixed-width and right-aligned so values line up across rows.
function ResourceRow({
  res, description, canEdit, onChange, onRemove,
}: {
  res: StrategicResource;
  description?: string | null;
  canEdit: boolean;
  onChange: (updater: (r: StrategicResource) => StrategicResource) => void;
  onRemove: () => void;
}) {
  const { t } = useT();
  const fromRcajo = res.resource_id != null;
  return (
    <div className="pq-res-wrap">
      <div className="pq-res">
        <span className="pq-res-main">
          <span className="pq-res-title">{res.resource_title}</span>
          {res.is_new && <span className="badge pq-badge-new" title={t('prequal.newResourceTitle')}>{t('prequal.new')}</span>}
          {fromRcajo && description && (
            <InfoPopover label={t('prequal.rcajoDescription')}>
              <div className="pq-pop-tag">RCAJO</div>
              <div className="pq-pop-body">{description}</div>
            </InfoPopover>
          )}
        </span>
        <span className="pq-res-mat">
          <span className="pq-mat-cur">{scoreLabel(res.current_score)}</span>
          <span className="pq-mat-arrow">→</span>
          {canEdit ? (
            <input
              className="pq-mat-input"
              type="number" step="0.5" min="0" max="5"
              value={res.target_score ?? ''}
              onChange={(e) => onChange(r => ({ ...r, target_score: Number(e.target.value) }))}
            />
          ) : (
            <span className="pq-mat-tgt">{scoreLabel(res.target_score)}</span>
          )}
        </span>
        <span className="pq-res-crit"><CriticalityBar value={res.criticality} /></span>
        <span className="pq-res-act">
          {canEdit && (
            <button className="tag-remove" onClick={onRemove} title={t('prequal.removeResource')}>
              <X className="icon-sm" style={{ width: 12, height: 12 }} />
            </button>
          )}
        </span>
      </div>
      {res.improvement && (
        <div className="pq-pistache"><span className="pq-src-tag pq-src-pistache">pistache</span>{res.improvement}</div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────
// Strategic panel (RCAJO capabilities + resources)
// ───────────────────────────────────────────────

export function StrategicPanel({ form, setForm, projectId, canEdit }: PanelProps) {
  const { t } = useT();
  const [inventory, setInventory] = useState<RcajoInventory | null>(null);
  const [inventoryLoaded, setInventoryLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState<string | null>(null);

  // Fetch RCAJO inventory once on mount.
  useEffect(() => {
    let active = true;
    api.getRcajoInventory()
      .then((inv: any) => { if (active) setInventory(inv); })
      .catch(() => { /* tolerate — show subtle note */ })
      .finally(() => { if (active) setInventoryLoaded(true); });
    return () => { active = false; };
  }, []);

  const rcajo = form.rcajo;
  const capabilities = rcajo?.capabilities ?? [];
  const standaloneResources = rcajo?.resources ?? [];
  // Strategic is a USER-controlled flag (pistache sets an initial value; the user
  // can add/remove it). It is no longer auto-derived from inventory membership.
  const isStrategic = !!rcajo?.is_strategic;
  const hasNewItems =
    capabilities.some(c => c.is_new || c.resources.some(r => r.is_new)) ||
    standaloneResources.some(r => r.is_new);

  const resDesc = (id: number | null) => id == null ? null : (inventory?.resources.find(r => r.id === id)?.description ?? null);

  // Replace form.rcajo via an updater; keep is_strategic in sync with edits.
  const updateRcajo = (updater: (r: StrategicAssessment) => StrategicAssessment) => {
    const current: StrategicAssessment = rcajo ?? { is_strategic: false, rationale: '', capabilities: [], resources: [] };
    setForm({ ...form, rcajo: updater(current) });
  };

  const toggleStrategic = () => updateRcajo(r => ({ ...r, is_strategic: !r.is_strategic }));

  const suggest = async () => {
    setLoading(true);
    setError(null);
    try {
      const a = await api.aiPrequalStrategic(projectId) as StrategicAssessment;
      setForm({ ...form, rcajo: a });
    } catch (e: any) {
      setError(e?.message || t('prequal.aiSuggestionFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Remove a capability, PRESERVING its resources by moving them into the
  // standalone list (de-dup by resource_id when non-null; is_new ones always kept).
  const removeCapability = (i: number) =>
    updateRcajo(r => {
      const moved = r.capabilities[i]?.resources ?? [];
      const existing = r.resources ?? [];
      const seenIds = new Set(existing.filter(x => x.resource_id != null).map(x => x.resource_id));
      const merged = [...existing];
      for (const res of moved) {
        if (res.resource_id != null) {
          if (seenIds.has(res.resource_id)) continue;
          seenIds.add(res.resource_id);
        }
        merged.push(res);
      }
      return { ...r, capabilities: r.capabilities.filter((_, idx) => idx !== i), resources: merged };
    });

  const changeCapability = (i: number, updater: (c: StrategicCapability) => StrategicCapability) =>
    updateRcajo(r => ({ ...r, capabilities: r.capabilities.map((c, idx) => (idx === i ? updater(c) : c)) }));

  const addCapabilityFromInventory = (cap: RcajoCapability) =>
    updateRcajo(r => ({
      ...r,
      capabilities: [...r.capabilities, {
        capability_id: cap.id, capability_title: cap.title, is_new: false,
        nature: cap.nature ?? null, domain: cap.domain ?? null, resources: [],
      }],
    }));

  const addNewCapability = (name: string) =>
    updateRcajo(r => ({
      ...r,
      capabilities: [...r.capabilities, { capability_id: null, capability_title: name, is_new: true, resources: [] }],
    }));

  // ── standalone resources ──
  const changeStandaloneRes = (ri: number, updater: (r: StrategicResource) => StrategicResource) =>
    updateRcajo(r => ({ ...r, resources: (r.resources ?? []).map((res, j) => (j === ri ? updater(res) : res)) }));

  const removeStandaloneRes = (ri: number) =>
    updateRcajo(r => ({ ...r, resources: (r.resources ?? []).filter((_, j) => j !== ri) }));

  const addStandaloneResourceFromInventory = (res: RcajoResource) =>
    updateRcajo(r => ({
      ...r,
      resources: [...(r.resources ?? []), {
        resource_id: res.id, resource_title: res.title, is_new: false, improvement: '',
        current_score: res.current_score ?? null, target_score: res.current_score ?? 0, criticality: null,
      }],
    }));

  const addNewStandaloneResource = (name: string) =>
    updateRcajo(r => ({
      ...r,
      resources: [...(r.resources ?? []), {
        resource_id: null, resource_title: name, is_new: true, improvement: '',
        current_score: null, target_score: 0, criticality: null,
      }],
    }));

  // Push every is_new item into RCAJO so the records get an id + origin.
  // Build next-state locally then setForm once (avoid stale-closure clobber).
  const pushNewToRcajo = async () => {
    if (!rcajo) return;
    setPushing(true);
    setPushResult(null);
    let pushed = 0, failed = 0;
    const next: StrategicAssessment = {
      ...rcajo,
      capabilities: rcajo.capabilities.map(c => ({ ...c, resources: c.resources.map(r => ({ ...r })) })),
      resources: (rcajo.resources ?? []).map(r => ({ ...r })),
    };
    const pushResource = async (res: StrategicResource) => {
      if (!(res.is_new && res.resource_id == null)) return;
      try {
        const created = await api.createRcajoResource({ title: res.resource_title, project_id: projectId }) as any;
        res.resource_id = created.id; res.is_new = false; pushed++;
      } catch { failed++; }
    };
    try {
      for (const cap of next.capabilities) {
        if (cap.is_new && cap.capability_id == null) {
          try {
            const created = await api.createRcajoCapability({ title: cap.capability_title, project_id: projectId }) as any;
            cap.capability_id = created.id; cap.is_new = false; pushed++;
          } catch { failed++; }
        }
        for (const res of cap.resources) await pushResource(res);
      }
      for (const res of next.resources ?? []) await pushResource(res);
      setForm({ ...form, rcajo: next });
      setPushResult(failed > 0 ? t('prequal.pushResultPartial', { pushed, failed }) : t('prequal.pushResultOk', { pushed }));
    } finally {
      setPushing(false);
    }
  };

  const availableCapabilities = inventory?.capabilities.filter(c => !capabilities.some(cc => cc.capability_id === c.id)) ?? [];
  const availableStandaloneResources = inventory?.resources.filter(r => !standaloneResources.some(rr => rr.resource_id === r.id)) ?? [];

  return (
    <div className="pq-panel">
      <div className="pq-panel-header">
        <div className="flex items-center gap-8">
          <span className="section-label">{t('prequal.strategicVerification')}</span>
          {canEdit ? (
            <button
              type="button"
              className={`pq-strategic-toggle${isStrategic ? ' on' : ''}`}
              onClick={toggleStrategic}
              title={isStrategic ? t('prequal.removeStrategicLabel') : t('prequal.markStrategicTitle')}
            >
              {isStrategic ? t('prequal.strategicOn') : t('prequal.strategicMark')}
            </button>
          ) : (
            isStrategic && <span className="badge badge-ok">{t('prequal.strategic')}</span>
          )}
        </div>
        <button className="btn btn-ghost btn-sm" type="button" onClick={suggest} disabled={loading || !canEdit}>
          {loading ? <RefreshCw size={14} className="spin" /> : null}
          {loading ? t('prequal.suggesting') : t('prequal.suggestWithPistache')}
        </button>
      </div>

      {error && <div className="pq-error" style={{ marginTop: 'var(--s8)' }}>{error}</div>}
      {inventoryLoaded && !inventory && (
        <div className="text-sm text-ink-3" style={{ marginTop: 'var(--s8)' }}>
          {t('prequal.inventoryUnavailable')}
        </div>
      )}

      {/* Assessment notes — auto-growing, frameless */}
      <div style={{ marginTop: 'var(--s12)' }}>
        <div className="pq-label-row">
          <span className="pq-sub-label">{t('prequal.assessmentNotes')}</span>
          {canEdit && !!rcajo?.rationale && (
            <button className="btn btn-ghost btn-sm" type="button" onClick={() => updateRcajo(r => ({ ...r, rationale: '' }))}>{t('common.clear')}</button>
          )}
        </div>
        {canEdit ? (
          <BareTextarea
            liveField="prequalNotes"
            value={rcajo?.rationale ?? ''}
            onChange={(v) => updateRcajo(r => ({ ...r, rationale: v }))}
            placeholder={t('prequal.assessmentNotesPlaceholder')}
          />
        ) : rcajo?.rationale ? (
          <div className="text-sm text-ink-2" style={{ whiteSpace: 'pre-wrap' }}>{rcajo.rationale}</div>
        ) : (
          <div className="text-sm text-ink-3">{t('prequal.noNotes')}</div>
        )}
      </div>

      {/* Capability cards */}
      {capabilities.length > 0 ? (
        <div className="flex flex-col gap-12" style={{ marginTop: 'var(--s12)' }}>
          {capabilities.map((cap, ci) => (
            <CapabilityCard
              key={ci} cap={cap} index={ci} inventory={inventory} canEdit={canEdit}
              onChange={changeCapability} onRemove={() => removeCapability(ci)}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state" style={{ marginTop: 'var(--s12)' }}>
          <div className="empty-state-desc">
            {t('prequal.noCapabilities')} {canEdit ? t('prequal.noCapabilitiesEdit') : ''}
          </div>
        </div>
      )}

      {/* Add capability — fuzzy search RCAJO or create new */}
      {canEdit && (
        <div className="pq-add" style={{ marginTop: 'var(--s8)' }}>
          <FuzzyAdd
            placeholder={t('prequal.addCapabilityPlaceholder')}
            options={availableCapabilities}
            getLabel={(c) => c.title}
            getSubLabel={(c) => [c.nature, c.domain].filter(Boolean).join(' · ') || null}
            onPickExisting={addCapabilityFromInventory}
            onAddNew={addNewCapability}
          />
        </div>
      )}

      {/* Standalone resources (kept when a capability is deleted) */}
      <div style={{ marginTop: 'var(--s16)' }}>
        <div className="pq-label-row">
          <span className="pq-sub-label">{t('prequal.standaloneResources')}</span>
          {standaloneResources.length > 0 && <span className="text-sm text-ink-3">{standaloneResources.length}</span>}
        </div>
        {standaloneResources.length > 0 && (
          <div className="card">
            <ResourceHeader />
            {standaloneResources.map((res, ri) => (
              <ResourceRow
                key={ri} res={res} description={resDesc(res.resource_id)} canEdit={canEdit}
                onChange={(updater) => changeStandaloneRes(ri, updater)} onRemove={() => removeStandaloneRes(ri)}
              />
            ))}
          </div>
        )}
        {canEdit && (
          <div className="pq-add" style={{ marginTop: 'var(--s8)' }}>
            <FuzzyAdd
              placeholder={t('prequal.addResourcePlaceholder')}
              options={availableStandaloneResources}
              getLabel={(r) => r.title}
              getSubLabel={(r) => r.current_score != null ? t('prequal.maturityShort', { score: scoreLabel(r.current_score) }) : null}
              onPickExisting={addStandaloneResourceFromInventory}
              onAddNew={addNewStandaloneResource}
            />
          </div>
        )}
      </div>

      {canEdit && hasNewItems && (
        <div className="pq-add" style={{ marginTop: 'var(--s12)' }}>
          <button className="btn btn-ghost btn-sm" type="button" onClick={pushNewToRcajo} disabled={pushing}>
            {pushing ? <RefreshCw size={14} className="spin" /> : null}{' '}{t('prequal.pushNewItems')}
          </button>
          {pushResult && <span className="text-sm text-ink-3" style={{ marginLeft: 'var(--s8)' }}>{pushResult}</span>}
        </div>
      )}
    </div>
  );
}

// ── Capability card (nested resources) ──

function CapabilityCard({ cap, index, inventory, canEdit, onChange, onRemove }: {
  cap: StrategicCapability;
  index: number;
  inventory: RcajoInventory | null;
  canEdit: boolean;
  onChange: (i: number, updater: (c: StrategicCapability) => StrategicCapability) => void;
  onRemove: () => void;
}) {
  const { t } = useT();
  const capDesc = cap.capability_id != null ? (inventory?.capabilities.find(c => c.id === cap.capability_id)?.description ?? null) : null;
  const resDesc = (id: number | null) => id == null ? null : (inventory?.resources.find(r => r.id === id)?.description ?? null);

  const changeRes = (ri: number, updater: (r: StrategicResource) => StrategicResource) =>
    onChange(index, c => ({ ...c, resources: c.resources.map((r, j) => (j === ri ? updater(r) : r)) }));
  const removeRes = (ri: number) =>
    onChange(index, c => ({ ...c, resources: c.resources.filter((_, j) => j !== ri) }));
  const addResourceFromInventory = (res: RcajoResource) =>
    onChange(index, c => ({
      ...c,
      resources: [...c.resources, {
        resource_id: res.id, resource_title: res.title, is_new: false, improvement: '',
        current_score: res.current_score ?? null, target_score: res.current_score ?? 0, criticality: null,
      }],
    }));
  const addNewResource = (name: string) =>
    onChange(index, c => ({
      ...c,
      resources: [...c.resources, {
        resource_id: null, resource_title: name, is_new: true, improvement: '',
        current_score: null, target_score: 0, criticality: null,
      }],
    }));

  const availableResources = inventory?.resources.filter(r => !cap.resources.some(rr => rr.resource_id === r.id)) ?? [];

  return (
    <div className="card">
      <div className="pq-cap-header">
        <div className="flex items-center gap-8" style={{ flexWrap: 'wrap' }}>
          <span className="font-medium text-ink-1">{cap.capability_title}</span>
          {cap.nature && <span className="badge">{cap.nature}</span>}
          {cap.domain && <span className="badge">{cap.domain}</span>}
          {cap.is_new && <span className="badge pq-badge-new">{t('prequal.newCapabilityBadge')}</span>}
          {capDesc && (
            <InfoPopover label={t('prequal.rcajoDescription')}>
              <div className="pq-pop-tag">RCAJO</div>
              <div className="pq-pop-body">{capDesc}</div>
            </InfoPopover>
          )}
        </div>
        {canEdit && (
          <button className="tag-remove" onClick={onRemove} title={t('prequal.removeCapability')}>
            <X className="icon-sm" style={{ width: 14, height: 14 }} />
          </button>
        )}
      </div>

      {cap.resources.length > 0 ? (
        <div style={{ marginTop: 'var(--s8)' }}>
          <ResourceHeader />
          {cap.resources.map((res, ri) => (
            <ResourceRow
              key={ri} res={res} description={resDesc(res.resource_id)} canEdit={canEdit}
              onChange={(updater) => changeRes(ri, updater)} onRemove={() => removeRes(ri)}
            />
          ))}
        </div>
      ) : (
        <div className="text-sm text-ink-3" style={{ marginTop: 'var(--s8)' }}>{t('prequal.noResources')}</div>
      )}

      {canEdit && (
        <div className="pq-add" style={{ marginTop: 'var(--s8)' }}>
          <FuzzyAdd
            placeholder={t('prequal.addResourcePlaceholder')}
            options={availableResources}
            getLabel={(r) => r.title}
            getSubLabel={(r) => r.current_score != null ? t('prequal.maturityShort', { score: scoreLabel(r.current_score) }) : null}
            onPickExisting={addResourceFromInventory}
            onAddNew={addNewResource}
          />
        </div>
      )}
    </div>
  );
}

// ───────────────────────────────────────────────
// Sequencing panel (dependencies & recommendation)
// ───────────────────────────────────────────────

type SeqProjectItem = { name: string; reason: string; project_id?: number | null };
type SeqResourceItem = { title: string; reason: string; resource_id?: number | null };

const EMPTY_SEQ: SequencingAssessment = {
  depends_on_projects: [], enables_projects: [],
  depends_on_resources: [], enables_resources: [],
  recommendation: 'sequence_later', recommendation_text: '', suggested_predecessors: [],
};

function mergeProjectItems(existing: SeqProjectItem[], incoming: SeqProjectItem[]): SeqProjectItem[] {
  const out = [...existing];
  for (const it of incoming) {
    const dup = out.some(x =>
      (x.project_id != null && it.project_id != null && x.project_id === it.project_id) ||
      x.name.trim().toLowerCase() === it.name.trim().toLowerCase());
    if (!dup) out.push(it);
  }
  return out;
}

function mergeResourceItems(existing: SeqResourceItem[], incoming: SeqResourceItem[]): SeqResourceItem[] {
  const out = [...existing];
  for (const it of incoming) {
    const dup = out.some(x =>
      (x.resource_id != null && it.resource_id != null && x.resource_id === it.resource_id) ||
      x.title.trim().toLowerCase() === it.title.trim().toLowerCase());
    if (!dup) out.push(it);
  }
  return out;
}

// Per-item "Ask pistache" button — lives inline on the card head.
function AskButton({ onAsk }: { onAsk?: () => Promise<void> }) {
  const { t } = useT();
  const [asking, setAsking] = useState(false);
  if (!onAsk) return null;
  const go = async () => { setAsking(true); try { await onAsk(); } finally { setAsking(false); } };
  return (
    <button type="button" className="pq-ask" onClick={go} disabled={asking} title={t('prequal.askPistacheTitle')}>
      {asking ? <RefreshCw size={12} className="spin" /> : <Sparkles size={12} />} {t('prequal.askPistache')}
    </button>
  );
}

// Frameless auto-growing reason field.
function DependencyReason({ item, canEdit, onReason }: {
  item: { reason: string };
  canEdit: boolean;
  onReason: (v: string) => void;
}) {
  const { t } = useT();
  if (!canEdit) return item.reason ? <div className="pq-dep-reason-ro">{item.reason}</div> : null;
  return (
    <div className="pq-dep-reason">
      <BareTextarea value={item.reason} onChange={onReason} placeholder={t('prequal.reasonPlaceholder')} />
    </div>
  );
}

// A project dependency card: project identity (name + stage + status placeholder)
// over a frameless auto-growing reasoning field.
function SeqProjectCard({ item, project, canEdit, onReason, onRemove, onAsk }: {
  item: SeqProjectItem;
  project: Project | undefined;
  canEdit: boolean;
  onReason: (v: string) => void;
  onRemove: () => void;
  onAsk?: () => Promise<void>;
}) {
  const { t } = useT();
  const stage = project?.status_label || null;
  const deliverables = project ? parseDeliverables(project.deliverables) : [];
  const hasInfo = !!(project && (project.problem || project.goal || deliverables.length));
  const progress = project ? getExecutionProgress(project.status_label || '') : 'before';
  // A project in Execution: its status from its milestones (late, ending soon, on time).
  const health = project && progress === 'execution' ? milestoneHealth(project.planning) : null;
  const statusBadge = progress === 'before'
    ? <span className="badge badge-muted" title={t('prequal.notInExecution')}>—</span>
    : progress === 'after'
    ? <span className="badge badge-ok" title={t('prequal.executionCompleted')}>✓</span>
    : health && (
      <span className={`badge ${HEALTH_BADGE_CLASS[health.level]}`} title={health.milestone}>
        {t(HEALTH_TITLE_KEY[health.level])}
      </span>
    );
  return (
    <div className="pq-dep-card">
      <div className="pq-dep-head">
        <span className="pq-dep-name">{project?.name || item.name}</span>
        {hasInfo && project && (
          <InfoPopover label={t('prequal.problemGoalDeliverables')}>
            <div className="pq-pop-title">{project.name}</div>
            {project.problem && <div className="pq-proj-info-row"><span className="pq-proj-info-k">{t('prequal.problemNeed')}</span><span>{project.problem}</span></div>}
            {project.goal && <div className="pq-proj-info-row"><span className="pq-proj-info-k">{t('prequal.goal')}</span><span>{project.goal}</span></div>}
            {deliverables.length > 0 && (
              <div className="pq-proj-info-row">
                <span className="pq-proj-info-k">{t('prequal.deliverables')}</span>
                <ul className="pq-proj-info-list">{deliverables.map((d) => <li key={d.id}>{d.title}</li>)}</ul>
              </div>
            )}
          </InfoPopover>
        )}
        <span className="pq-dep-tags">
          {stage && <span className="badge" title={t('prequal.pipelineStageTitle')}>{stage}</span>}
          {statusBadge}
          {canEdit && <AskButton onAsk={onAsk} />}
        </span>
        {canEdit && (
          <button className="tag-remove" onClick={onRemove} title={t('common.remove')}>
            <X className="icon-sm" style={{ width: 12, height: 12 }} />
          </button>
        )}
      </div>
      <DependencyReason item={item} canEdit={canEdit} onReason={onReason} />
    </div>
  );
}

// A resource dependency card: resource identity (+ RCAJO info) over a frameless reason.
function SeqResourceCard({ item, description, canEdit, onReason, onRemove, onAsk }: {
  item: SeqResourceItem;
  description?: string | null;
  canEdit: boolean;
  onReason: (v: string) => void;
  onRemove: () => void;
  onAsk?: () => Promise<void>;
}) {
  const { t } = useT();
  return (
    <div className="pq-dep-card">
      <div className="pq-dep-head">
        <span className="pq-dep-name">{item.title}</span>
        {item.resource_id != null && description && (
          <InfoPopover label={t('prequal.rcajoDescription')}>
            <div className="pq-pop-tag">RCAJO</div>
            <div className="pq-pop-body">{description}</div>
          </InfoPopover>
        )}
        <span className="pq-dep-tags">
          {item.resource_id == null && <span className="text-sm text-ink-3" title={t('prequal.notInRcajoTitle')}>{t('prequal.notInRcajo')}</span>}
          {canEdit && <AskButton onAsk={onAsk} />}
        </span>
        {canEdit && (
          <button className="tag-remove" onClick={onRemove} title={t('common.remove')}>
            <X className="icon-sm" style={{ width: 12, height: 12 }} />
          </button>
        )}
      </div>
      <DependencyReason item={item} canEdit={canEdit} onReason={onReason} />
    </div>
  );
}

// Read-only left→right flow: predecessors → this project → successors. Nodes show stage.
function DependencyGraph({ projectName, depends, enables, projectsById }: {
  projectName: string;
  depends: SeqProjectItem[];
  enables: SeqProjectItem[];
  projectsById: Map<number, Project>;
}) {
  const { t } = useT();
  const node = (it: SeqProjectItem, key: number) => {
    const p = it.project_id != null ? projectsById.get(it.project_id) : undefined;
    return (
      <div className="pq-node" key={key}>
        <span className="pq-node-name">{p?.name || it.name}</span>
        {p?.status_label && <span className="pq-node-stage">{p.status_label}</span>}
      </div>
    );
  };
  return (
    <div className="pq-graph">
      <div className="pq-graph-col">
        {depends.length > 0 ? depends.map(node) : <div className="pq-node pq-node--muted">{t('prequal.noPredecessors')}</div>}
      </div>
      <div className="pq-arrow" aria-hidden>→</div>
      <div className="pq-graph-col pq-graph-col--self">
        <div className="pq-node pq-node--self"><span className="pq-node-name">{projectName?.trim() || t('prequal.thisProject')}</span></div>
      </div>
      <div className="pq-arrow" aria-hidden>→</div>
      <div className="pq-graph-col">
        {enables.length > 0 ? enables.map(node) : <div className="pq-node pq-node--muted">{t('prequal.noSuccessors')}</div>}
      </div>
    </div>
  );
}

export function SequencingPanel({ form, setForm, projectId, canEdit }: PanelProps) {
  const { t } = useT();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [inv, setInv] = useState<RcajoInventory | null>(null);

  useEffect(() => {
    let active = true;
    api.getProjects()
      .then((ps: any) => { if (active) setProjects(Array.isArray(ps) ? ps as Project[] : (ps?.projects ?? [])); })
      .catch(() => {});
    api.getRcajoInventory()
      .then((i: any) => { if (active) setInv(i); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const seq = form.sequencing;
  const depProjects = seq?.depends_on_projects ?? [];
  const enProjects = seq?.enables_projects ?? [];
  const depResources = seq?.depends_on_resources ?? [];
  const projectName = form.name ?? '';

  const projectsById = new Map(projects.map(p => [p.id, p]));
  const findProject = (id?: number | null) => id == null ? undefined : projectsById.get(id);
  const resDesc = (id?: number | null) => id == null ? null : (inv?.resources.find(r => r.id === id)?.description ?? null);

  const updateSeq = (updater: (s: SequencingAssessment) => SequencingAssessment) => {
    const current = seq ?? EMPTY_SEQ;
    setForm({ ...form, sequencing: updater(current) });
  };

  // ── project groups ──
  // An edge is declared by ONE project; whatever another project declared about
  // this one is shown here read-only, and can't be declared a second time.
  const incoming = useMemo(() => incomingLinksFor(projectId, projects), [projectId, projects]);
  const incomingBlockerIds = new Set(incoming.blockers.map(l => l.projectId));
  const incomingBlockedIds = new Set(incoming.blocked.map(l => l.projectId));

  const otherProjects = projects.filter(p => p.id !== projectId);
  const depProjAvail = otherProjects.filter(p => !depProjects.some(x => x.project_id === p.id) && !incomingBlockerIds.has(p.id));
  const enProjAvail = otherProjects.filter(p => !enProjects.some(x => x.project_id === p.id) && !incomingBlockedIds.has(p.id));

  const addDepProj = (p: Project) => updateSeq(s => ({ ...s, depends_on_projects: mergeProjectItems(s.depends_on_projects, [{ name: p.name, project_id: p.id, reason: '' }]) }));
  const addEnProj = (p: Project) => updateSeq(s => ({ ...s, enables_projects: mergeProjectItems(s.enables_projects, [{ name: p.name, project_id: p.id, reason: '' }]) }));
  const changeDepProjReason = (i: number, v: string) => updateSeq(s => ({ ...s, depends_on_projects: s.depends_on_projects.map((x, j) => (j === i ? { ...x, reason: v } : x)) }));
  const changeEnProjReason = (i: number, v: string) => updateSeq(s => ({ ...s, enables_projects: s.enables_projects.map((x, j) => (j === i ? { ...x, reason: v } : x)) }));
  const removeDepProj = (i: number) => updateSeq(s => ({ ...s, depends_on_projects: s.depends_on_projects.filter((_, j) => j !== i) }));
  const removeEnProj = (i: number) => updateSeq(s => ({ ...s, enables_projects: s.enables_projects.filter((_, j) => j !== i) }));

  // ── resource groups ──
  const depResAvail = inv?.resources?.filter(r => !depResources.some(x => x.resource_id === r.id)) ?? [];

  const addDepRes = (r: RcajoResource) => updateSeq(s => ({ ...s, depends_on_resources: mergeResourceItems(s.depends_on_resources, [{ title: r.title, resource_id: r.id, reason: '' }]) }));
  const addNewDepRes = (name: string) => updateSeq(s => ({ ...s, depends_on_resources: mergeResourceItems(s.depends_on_resources, [{ title: name, resource_id: null, reason: '' }]) }));
  const changeDepResReason = (i: number, v: string) => updateSeq(s => ({ ...s, depends_on_resources: s.depends_on_resources.map((x, j) => (j === i ? { ...x, reason: v } : x)) }));
  const removeDepRes = (i: number) => updateSeq(s => ({ ...s, depends_on_resources: s.depends_on_resources.filter((_, j) => j !== i) }));

  // Pistache check — MERGE AI suggestions onto existing user entries; overwrite scalar fields.
  const check = async () => {
    setLoading(true);
    setError(null);
    try {
      const ai = await api.aiPrequalSequencing(projectId) as SequencingAssessment;
      const current = seq ?? EMPTY_SEQ;
      setForm({ ...form, sequencing: {
        depends_on_projects: mergeProjectItems(current.depends_on_projects, (ai.depends_on_projects ?? []).map(it => ({ name: it.name, reason: it.reason, project_id: (it as any).project_id ?? null }))),
        enables_projects: mergeProjectItems(current.enables_projects, (ai.enables_projects ?? []).map(it => ({ name: it.name, reason: it.reason, project_id: (it as any).project_id ?? null }))),
        depends_on_resources: mergeResourceItems(current.depends_on_resources, (ai.depends_on_resources ?? []).map(it => ({ title: it.title, reason: it.reason, resource_id: (it as any).resource_id ?? null }))),
        enables_resources: current.enables_resources, // surfaced in Strategic Verification, not here
        recommendation: ai.recommendation ?? current.recommendation,
        recommendation_text: ai.recommendation_text ?? current.recommendation_text,
        suggested_predecessors: ai.suggested_predecessors ?? current.suggested_predecessors,
      } });
    } catch (e: any) {
      setError(e?.message || t('prequal.sequencingCheckFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Ask pistache to draft the reason for ONE dependency, then apply it to that item.
  const askReason = async (relation: string, name: string, id: number | null, apply: (v: string) => void) => {
    const r = await api.aiDependencyReason({ project_id: projectId, relation, target_name: name, target_id: id }) as any;
    if (r?.reason) apply(r.reason);
  };

  const projGroup = (
    title: string, relation: string, items: SeqProjectItem[], avail: Project[],
    onAdd: (p: Project) => void, onReason: (i: number, v: string) => void, onRemove: (i: number) => void,
    fromOthers: ProjectLinkView[] = [],
  ) => (
    <div>
      <div className="pq-sub-label">{title}</div>
      {items.length > 0 && (
        <div className="flex flex-col gap-8">
          {items.map((it, i) => {
            const p = findProject(it.project_id);
            return (
              <SeqProjectCard key={i} item={it} project={p} canEdit={canEdit}
                onReason={(v) => onReason(i, v)} onRemove={() => onRemove(i)}
                onAsk={() => askReason(relation, it.name, it.project_id ?? null, (v) => onReason(i, v))} />
            );
          })}
        </div>
      )}
      {fromOthers.length > 0 && (
        <div className="flex flex-col gap-8" style={{ marginTop: items.length > 0 ? 'var(--s8)' : 0 }}>
          <div className="text-sm text-ink-3" style={{ fontStyle: 'italic' }}>{t('prequal.declaredElsewhere')}</div>
          {fromOthers.map((l, i) => (
            <SeqProjectCard key={`in-${i}`} item={{ name: l.name, reason: l.reason, project_id: l.projectId }}
              project={findProject(l.projectId)} canEdit={false}
              onReason={() => {}} onRemove={() => {}} />
          ))}
        </div>
      )}
      {canEdit && (
        <div className="pq-add" style={{ marginTop: 'var(--s8)' }}>
          <FuzzyAdd
            placeholder={t('prequal.addProjectPlaceholder')}
            options={avail}
            getLabel={(p) => p.name}
            getSubLabel={(p) => p.status_label || null}
            onPickExisting={onAdd}
            allowNew={false}
          />
        </div>
      )}
    </div>
  );

  const resGroup = (
    title: string, relation: string, items: SeqResourceItem[], avail: RcajoResource[],
    onAdd: (r: RcajoResource) => void, onAddNew: (n: string) => void,
    onReason: (i: number, v: string) => void, onRemove: (i: number) => void,
  ) => (
    <div>
      <div className="pq-sub-label">{title}</div>
      {items.length > 0 && (
        <div className="flex flex-col gap-8">
          {items.map((it, i) => (
            <SeqResourceCard key={i} item={it} description={resDesc(it.resource_id)} canEdit={canEdit}
              onReason={(v) => onReason(i, v)} onRemove={() => onRemove(i)}
              onAsk={() => askReason(relation, it.title, it.resource_id ?? null, (v) => onReason(i, v))} />
          ))}
        </div>
      )}
      {canEdit && (
        <div className="pq-add" style={{ marginTop: 'var(--s8)' }}>
          <FuzzyAdd
            placeholder={t('prequal.addResourceSearchPlaceholder')}
            options={avail}
            getLabel={(r) => r.title}
            getSubLabel={(r) => r.current_score != null ? t('prequal.maturityShort', { score: scoreLabel(r.current_score) }) : null}
            onPickExisting={onAdd}
            onAddNew={onAddNew}
            createText={(q) => t('prequal.referenceNotInRcajo', { q })}
          />
        </div>
      )}
    </div>
  );

  return (
    <div className="pq-panel">
      <div className="pq-panel-header">
        <span className="section-label">{t('prequal.dependenciesSequencing')}</span>
        <button className="btn btn-ghost btn-sm" type="button" onClick={check} disabled={loading || !canEdit}>
          {loading ? <RefreshCw size={14} className="spin" /> : null}
          {loading ? t('prequal.checking') : t('prequal.checkWithPistache')}
        </button>
      </div>

      {error && <div className="pq-error" style={{ marginTop: 'var(--s8)' }}>{error}</div>}

      {/* Dependency diagram */}
      <div style={{ marginTop: 'var(--s12)' }}>
        <div className="pq-sub-label">{t('prequal.flow')}</div>
        <DependencyGraph
          projectName={projectName}
          depends={[...depProjects, ...incoming.blockers.map(l => ({ name: l.name, reason: l.reason, project_id: l.projectId }))]}
          enables={[...enProjects, ...incoming.blocked.map(l => ({ name: l.name, reason: l.reason, project_id: l.projectId }))]}
          projectsById={projectsById} />
      </div>

      <div className="flex flex-col gap-16" style={{ marginTop: 'var(--s16)' }}>
        {projGroup(t('prequal.dependsOnProjects'), 'depends_on_project', depProjects, depProjAvail, addDepProj, changeDepProjReason, removeDepProj, incoming.blockers)}
        {projGroup(t('prequal.enablesProjects'), 'enables_project', enProjects, enProjAvail, addEnProj, changeEnProjReason, removeEnProj, incoming.blocked)}
        {/* "Depends on — resources" removed as superfluous (#20). The underlying
            data plumbing (depends_on_resources) is kept for backward compatibility. */}
      </div>

      {/* Recommendation (AI output) */}
      {seq && (seq.recommendation_text || seq.suggested_predecessors?.length > 0) && (
        <div style={{ marginTop: 'var(--s16)' }}>
          <div className="pq-recommendation">
            <span className={`badge ${seq.recommendation === 'do_now' ? 'badge-ok' : 'badge-warn'}`}>
              {seq.recommendation === 'do_now' ? t('prequal.doNow') : t('prequal.sequenceLater')}
            </span>
            {seq.recommendation_text && (
              <span className="text-sm text-ink-2" style={{ marginLeft: 'var(--s8)' }}>
                <span className="pq-src-tag pq-src-pistache">pistache</span>{seq.recommendation_text}
              </span>
            )}
          </div>
          {seq.suggested_predecessors?.length > 0 && (
            <div className="flex items-center gap-8" style={{ marginTop: 'var(--s8)', flexWrap: 'wrap' }}>
              <span className="text-sm text-ink-3">{t('prequal.suggestedPredecessors')}</span>
              {seq.suggested_predecessors.map((p, i) => <span key={i} className="badge">{p}</span>)}
            </div>
          )}
        </div>
      )}

    </div>
  );
}
